import React, {useEffect, useState, useMemo} from 'react'
import { useWallet, useConnection } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { encodeURL, createQR } from '@solana/pay';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { ConnectionProvider, WalletProvider } from '@solana/wallet-adapter-react';
import { PhantomWalletAdapter } from '@solana/wallet-adapter-phantom';
import { PublicKey, SystemProgram, Transaction, LAMPORTS_PER_SOL, Connection } from '@solana/web3.js';
import { getSolanaRecentBlockhash } from '../util/brc20-api';
import { useSelector, useDispatch } from 'react-redux';
import { DocumentDuplicateIcon, WalletIcon, ArrowRightEndOnRectangleIcon } from '@heroicons/react/24/outline';
import { setSolanaAddress } from '../store/action';
import { formatAddress, formatAddressMini } from '../util/format-data';
import phantomImg from '../assets/imgs/phantom.png';
import solflareImg from '../assets/imgs/solflare.png';

import Solflare from '@solflare-wallet/sdk';

import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Typography,
  Button,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Input,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Popover,
  PopoverHandler,
  PopoverContent,
  Spinner
} from "@material-tailwind/react";

export default function SolanaConnect() {
  const { publicKey, connect, select, disconnect: solanaDisconnect } = useWallet();

  const { t, i18n } = useTranslation();
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const solanaWallet = useSelector(state => state.solanaWallet);
  const dispatch = useDispatch();

  const solflareWallet = new Solflare();

  solflareWallet.on('connect', () => {
    dispatch(setSolanaAddress({
      publicKey : solflareWallet.publicKey,
      address : solflareWallet.publicKey.toString(),
      provider : "solflare",
      wallet : solflareWallet
    }));
  });

  solflareWallet.on('disconnect', () => {
    dispatch(setSolanaAddress({
      publicKey : {},
      address : "",
      provider : "",
      wallet: {}
    }));
  });

  const phantomWalletConnect = async () => {
    setLoading(true);
    try {
      if (!window.solana?.isPhantom) {
        toast.error(t('Install Phantom Wallet Error Message'));
        return;
      }
      
      const provider = window.phantom.solana;
      const { publicKey } = await provider.connect();

      const connection = new Connection(process.env.REACT_APP_SOLANA_RPC_URL);

      handleCancel();
      dispatch(setSolanaAddress({
        publicKey : publicKey,
        provider : "phantom",
        address : publicKey?.toBase58() || "",
        wallet: provider
      }));
    } catch (error) {
      console.error('Phantom connection error:', error);
      toast.error(error.message);
      setLoading(false);
    }
    setLoading(false);
  }

  const solflareWalletConnect = async () => {
    setLoading(true);
    try {
      if (!window.solflare?.isSolflare) {
        toast.error(t('Install Solflare Wallet Error Message'));
        return;
      }

      await solflareWallet.connect();
      handleCancel();
    } catch (error) {
      console.error('Solflare connection error:', error);
      toast.error(error.message);
      setLoading(false);
    }
    setLoading(false);
  }

  const magicEdenWalletConnect = async () => {
    setLoading(true);
    try {
      const provider = window.magicEden?.solana;
      if (!provider) {
        toast.error(t('Install MagicEden Wallet Error Message'));
        return;
      }
      const { publicKey } = await provider.connect();

      handleCancel();
      dispatch(setSolanaAddress({
        publicKey : publicKey,
        provider : "magiceden",
        address : publicKey?.toBase58() || "",
        wallet: provider
      }));
    }
    catch(error) {
      console.log(error);
      setLoading(false);
    }
    setLoading(false);
  }

  const disconnectWallet = async () => {
    if (solanaWallet.provider == "phantom") {
      const provider = window.phantom.solana;
      await provider.disconnect();
    }
    else if (solanaWallet.provider == "solflare") {
      await solflareWallet.disconnect();
    }
    else if (solanaWallet.provider == "magiceden") {
      const provider = window.magicEden?.solana;
      await provider.disconnect();
    }

    dispatch(setSolanaAddress({
      publicKey : {},
      address : "",
      provider : "",
      wallet: {}

    }));
  }

  const handleCopy = (value) => {
    navigator.clipboard.writeText(value).then(
      () => {
        // Successfully copied to clipboard
        toast.success(t('Address Copied To Clipboard Success Message'))
      },
      (err) => {
        // Failed to copy to clipboard
        console.error('Could not copy address: ', err);
      }
    );
  }

  const handleOpen = (value) => {
    setShow(value);
    setLoading(false);
  }

  const handleCancel = () => {
    handleOpen(false);
  }
  return (
    <>
    {
      solanaWallet.address == "" ?
      <div className="flex flex-row bg-primary sm:px-4 px-[6px] py-[10px] rounded-md cursor-pointer hover:bg-primary-hover items-center justify-center text-white gap-1 font-bold" onClick={() => {handleOpen(true)}}>
        <WalletIcon strokeWidth={2} className="h-5 w-5" />
        <span className="text-[16px] mt-[2px]">{t('Connect')}</span>
      </div>
      :
      <div className="flex flex-row bg-primary sm:px-4 px-[6px] py-[10px] rounded-md cursor-pointer hover:bg-primary-hover gap-1 items-center text-white font-bold">
        <DocumentDuplicateIcon className="h-5 w-5 cursor-pointer" onClick={() => {handleCopy(solanaWallet.address)}} />
        <span className="font-bold sm:flex hidden text-[16px] mt-[2px]">{formatAddress(solanaWallet.address)}</span>
        <span className="font-bold sm:hidden flex text-[16px] mt-[2px]">{formatAddressMini(solanaWallet.address)}</span>
        <ArrowRightEndOnRectangleIcon className="h-6 w-6 cursor-pointer" onClick={() => {disconnectWallet()}} />
      </div>
    }
    <Dialog
        open={show}
        size={"xs"}
        handler={() => handleOpen(false)}
        
      >
        <DialogHeader>
          <div className="flex flex-row w-full justify-center mt-5 text-[28px] font-bold">{t('Select Wallet')}</div>
        </DialogHeader>
        <DialogBody>
          {
            loading ?
            <div className="flex flex-row w-full justify-center mt-5 text-[28px] font-bold">
              <Spinner className="h-12 w-12" />
            </div>
            :
            <div className="flex flex-col gap-3 sm:px-10 px-3">

              <div className="flex flex-row w-full items-center border-solid border-[1px] border-black rounded-lg hover:bg-[#F0F0F0] px-6 py-2 cursor-pointer" onClick={phantomWalletConnect}>
                <img src={phantomImg} className="w-[50px] h-[50px] bg-black p-2 rounded-md"/>
                <div className=" text-black font-semibold text-center text-[20px] w-full">Phantom Wallet</div>
              </div>
              <div className="flex flex-row w-full items-center border-solid border-[1px] border-black rounded-lg hover:bg-[#F0F0F0] px-6 py-2 cursor-pointer" onClick={solflareWalletConnect}>
                <img src={solflareImg} className="w-[50px] h-[50px] bg-black p-2 rounded-md"/>
                <div className=" text-black font-semibold text-center text-[20px] w-full">Solflare Wallet</div>
              </div>
              <div className="flex flex-row w-full items-center border-solid border-[1px] border-black rounded-lg hover:bg-[#F0F0F0] px-6 py-2 cursor-pointer" onClick={magicEdenWalletConnect}>
                <img src={"https://next-cdn.unisat.io/_/122/img/wallet/magic-eden.png"} className="w-[50px] h-[50px] bg-black p-2 rounded-md"/>
                <div className=" text-black font-semibold text-center text-[20px] w-full">Magic Eden</div>
              </div>
            </div>
          }
        </DialogBody>
        <DialogFooter>
          <Button
            onClick={() => {handleCancel()}}
            className="py-[12px] mr-3"
          >
            <span>{t('Cancel')}</span>
          </Button>
        </DialogFooter>
      </Dialog>
    </>
  )
}
